import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

export interface UndoAction {
  id: string;
  label: string;
  sublabel?: string;
  timestamp: number;
  undone: boolean;
  undoing: boolean;
  perform: () => Promise<void>;
  onUndone?: () => void;
}

interface UndoHistoryContextValue {
  actions: UndoAction[];
  push: (label: string, perform: () => Promise<void>, sublabel?: string, onUndone?: () => void) => void;
  undo: (id: string) => Promise<void>;
  pendingCount: number;
}

const UndoHistoryContext = createContext<UndoHistoryContextValue | null>(null);

export function UndoHistoryProvider({ children }: { children: React.ReactNode }) {
  const [actions, setActions] = useState<UndoAction[]>([]);
  const actionsRef = useRef<UndoAction[]>([]);

  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  const push = useCallback((label: string, perform: () => Promise<void>, sublabel?: string, onUndone?: () => void) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setActions((prev) =>
      [{ id, label, sublabel, timestamp: Date.now(), undone: false, undoing: false, perform, onUndone }, ...prev].slice(0, 10)
    );
  }, []);

  const undo = useCallback(async (id: string) => {
    const action = actionsRef.current.find((a) => a.id === id);
    if (!action || action.undone || action.undoing) return;
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, undoing: true } : a)));
    try {
      await action.perform();
      setActions((prev) => prev.map((a) => (a.id === id ? { ...a, undoing: false, undone: true } : a)));
      action.onUndone?.();
    } catch {
      setActions((prev) => prev.map((a) => (a.id === id ? { ...a, undoing: false } : a)));
    }
  }, []);

  const pendingCount = actions.filter((a) => !a.undone).length;

  return (
    <UndoHistoryContext.Provider value={{ actions, push, undo, pendingCount }}>
      {children}
    </UndoHistoryContext.Provider>
  );
}

export function useUndoHistory() {
  const ctx = useContext(UndoHistoryContext);
  if (!ctx) throw new Error('useUndoHistory must be used within UndoHistoryProvider');
  return ctx;
}
